import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert, lucideGoal } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, LockedQuoteAlertComponent, WithLockableFields } from 'app/home/create-quote/form-locking';
import { CoApplicantQuoteFormInput } from 'app/home/create-quote/forms/co-applicant/co-applicant.types';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { ContactDialogService } from 'app/home/create-quote/services/contact-dialog/contact-dialog.service';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-co-applicant',
  imports: [ReactiveFormsModule, NgIconComponent, FormCardComponent, ReadonlyRadioDirective, LockedQuoteAlertComponent, FocusOnlyRadioGroupDirective],
  providers: [provideIcons({ lucideGoal, lucideCircleAlert })],
  templateUrl: './co-applicant.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/co-applicant',
  isAutoAdvanced: true,
})
export class QuoteFormCoApplicantComponent extends BaseQuoteForm<CoApplicantQuoteFormInput> implements OnInit, Lockable {
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;

  protected contactDialogService = inject(ContactDialogService);

  public formDataIn: CoApplicantQuoteFormInput = this.quoteDraftStore.hasCoApplicantQuoteFormSelector();

  public formDefinition = {
    hasCoApplicant: new FormControl<boolean | undefined>(undefined, Validators.required),
  };
  public coApplicantForm = new FormGroup(this.formDefinition);

  public get showError(): boolean {
    if (this.isAutoAdvanceEnabled()) {
      return false;
    }
    const control = this.coApplicantForm.get('hasCoApplicant');
    return !!control?.invalid && !!control?.touched;
  }

  public override autoAdvanceControlRef = this.coApplicantForm.controls.hasCoApplicant;

  public getFormControls() {
    return this.coApplicantForm.controls;
  }

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('hasCoApplicant')) {
      this.coApplicantForm.controls.hasCoApplicant.setValue(this.formDataIn.hasCoApplicant);
    }
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  private getFormValues(): Pick<QuoteDraftState, 'hasCoApplicant'> {
    const { controls } = this.coApplicantForm;
    const formValues = {
      hasCoApplicant: controls.hasCoApplicant.value ?? undefined,
    };

    return formValues;
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.coApplicantForm.markAllAsTouched();
    if (this.coApplicantForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }
}
